import ContentHeader from '../../components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from '../../constants/master-list-tabs';
import CareAreaList from './CareAreaList/CareAreaList';
import CareAreaService from '../../services/CareAreaService';
import { ReactQueryKeys } from '../../constants/react-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import { CareArea } from '../../interfaces/care-area';
import SwalAlert, {
  firePreConfirmAlert,
} from '../../components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from '../../constants/roles';
import { useSelector } from 'react-redux';
import intersection from 'lodash.intersection';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { selectUser } from 'store/slices/auth';

const CareAreas = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const user = useSelector(selectUser);
  const canManage = !!intersection([user?.roles], [ADMINISTRATION, AUTHOR])
    .length;

  const {
    data: careAreas,
    isLoading: isLoadingCareAreas,
    isFetching,
  } = useQuery(
    [
      ReactQueryKeys.CARE_AREAS,
      {
        columnFilters,
        pagination,
      },
    ],
    () =>
      CareAreaService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),
    { keepPreviousData: true },
  );

  /**
   * Update search filters for the api call
   *
   * @param value The search value
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  // Show alert for confirming of deletion a record
  const handleDeleteShow = (careArea: CareArea) => {
    firePreConfirmAlert({
      title: t('care_areas.messages.modal__delete__title'),
      html: t('care_areas.messages.modal__delete__content', {
        name: careArea.name,
      }),
      preConfirm: () => {
        return CareAreaService.delete(String(careArea.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      if (data.isConfirmed) {
        queryClient.invalidateQueries([ReactQueryKeys.CARE_AREAS]);
        navigate('/care-areas');
        toast.success(t('care_areas.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs">
        <div className="row">
          <div className="col-3">
            {isLoadingCareAreas ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  translationKey="care_areas"
                  total={careAreas?.data.total}
                />

                <CareAreaList
                  refetch={isLoadingCareAreas || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  careAreasResponse={careAreas?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default CareAreas;
